import { DocumentText1, Clock, CloseCircle, SunFog, PauseCircle, AddCircle, Airplane } from "iconsax-react";
import CheckIcon from "@/shared/assets/icons/CheckIcon";
import { Badge } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { LeaveStatus, LeaveType, BusinessTripStatus, LeaveApplication } from "../types";

export const LEAVE_STATUS_MAP: Record<
  LeaveStatus,
  {
    label: string;
    color: "gray" | "info" | "positive" | "notice" | "negative";
    icon: React.ReactNode;
  }
> = {
  draft: {
    label: "statusLabels.draft",
    color: "info",
    icon: <DocumentText1 size={14} color="currentColor" />,
  },
  pending_review: {
    label: "statusLabels.pending_review",
    color: "notice",
    icon: <Clock size={14} color="currentColor" variant="Bold" />,
  },
  signing_pending: {
    label: "statusLabels.signing_pending",
    color: "notice",
    icon: <Clock size={14} color="currentColor" variant="Bold" />,
  },
  app_approved: {
    label: "statusLabels.app_approved",
    color: "notice",
    icon: <Clock size={14} color="currentColor" variant="Bold" />,
  },
  order_pending: {
    label: "statusLabels.order_pending",
    color: "notice",
    icon: <Clock size={14} color="currentColor" variant="Bold" />,
  },
  order_uploaded: {
    label: "statusLabels.order_uploaded",
    color: "positive",
    icon: <CheckIcon />,
  },
  order_acknowledged: {
    label: "statusLabels.order_acknowledged",
    color: "positive",
    icon: <CheckIcon />,
  },
  active: {
    label: "statusLabels.active",
    color: "positive",
    icon: <CheckIcon />,
  },
  completed: {
    label: "statusLabels.completed",
    color: "gray",
    icon: <CheckIcon />,
  },
  cancelled: {
    label: "statusLabels.cancelled",
    color: "negative",
    icon: <CloseCircle size={14} color="currentColor" variant="Bold" />,
  },
} as const;

export const LEAVE_TYPE_MAP: Record<
  LeaveType,
  {
    label: string;
    color: "gray" | "info" | "positive" | "notice" | "negative";
    icon: React.ReactNode;
  }
> = {
  annual: {
    label: "cards.leaveTypeLabels.annual",
    color: "gray",
    icon: <SunFog size={14} color="currentColor" variant="Bold" />,
  },
  unpaid: {
    label: "cards.leaveTypeLabels.unpaid",
    color: "notice",
    icon: <PauseCircle size={14} color="currentColor" variant="Bold" />,
  },
  medical: {
    label: "cards.leaveTypeLabels.medical",
    color: "negative",
    icon: <AddCircle size={14} color="currentColor" variant="Bold" />,
  },
  business_trip: {
    label: "cards.leaveTypeLabels.business_trip",
    color: "info",
    icon: <Airplane size={14} color="currentColor" variant="Bold" />,
  },
} as const;

export const BUSINESS_TRIP_STATUS_MAP: Record<
  BusinessTripStatus,
  {
    color: "gray" | "info" | "positive" | "notice" | "negative";
    icon: React.ReactNode;
  }
> = {
  pending: {
    color: "notice",
    icon: <Clock size={14} color="currentColor" variant="Bold" />,
  },
  approved: {
    color: "positive",
    icon: <CheckIcon />,
  },
  order_pending: {
    color: "notice",
    icon: <Clock size={14} color="currentColor" variant="Bold" />,
  },
  order_acknowledged: {
    color: "positive",
    icon: <CheckIcon />,
  },
  issued: {
    color: "positive",
    icon: <CheckIcon />,
  },
  settled: {
    color: "positive",
    icon: <CheckIcon />,
  },
} as const;

export function getBusinessTripStatusBadge(
  status: BusinessTripStatus,
  t: (key: string) => string,
  variant: "soft" | "outline" = "soft",
): React.ReactNode {
  const config = BUSINESS_TRIP_STATUS_MAP[status] ?? {
    color: "gray" as const,
    icon: <DocumentText1 size={14} color="currentColor" />,
  };
  return (
    <Badge
      variant={variant}
      color={config.color}
      text={t(`statusLabels.business_trip_${status}`)}
      icon={config.icon}
    />
  );
}

export function getStatusText(
  leave: LeaveApplication,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (leave.leave_type === "business_trip") return "";
  const statusConfig = LEAVE_STATUS_MAP[leave.status];
  if (!statusConfig) return "";

  if (leave.status === "active") {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    if (leave.start_date && leave.start_date > todayStr) {
      const formattedDate = formatDateForDisplay(leave.start_date, false);
      return t("statusLabels.activeFrom", { date: formattedDate });
    }

    return t(`statusLabels.active_${leave.leave_type}`);
  }

  if (leave.status === "order_acknowledged") {
    if (leave.leave_type === "medical") {
      return t("statusLabels.order_acknowledged_medical");
    }
    const formattedDate = formatDateForDisplay(leave.start_date, false);
    return t("statusLabels.order_acknowledged_with_date", { date: formattedDate });
  }

  if (leave.status === "completed") {
    return t(`statusLabels.completed_${leave.leave_type}`);
  }

  return t(statusConfig.label);
}
